import { useTranslation } from 'react-i18next';

import type { FormInstance } from '@arco-design/web-react';

import {
  ContainerSimpleCpuFields,
  CpuConfigFields,
  GpuExtraPersistFields,
  MemoryConfigFields,
} from '../../../components/computeConfigFields';
import { DEFAULT_QUANTITY_UNITS } from '../../../components/quantityUnit';
import { ResourceConfigModal } from '../../../components/resourceConfig';
import { hasVendorDeviceSelection } from '../../../components/vendorAndDeviceRadioList';
import { readVendorDeviceMeta } from '../../../components/vendorDevice';
import { COMPUTE_RESOURCE_MODAL_TITLE } from '../constants';
import { resolveVendorDevicePath } from '../domain/paths';
import { resolveComputeResourceCodes } from '../domain/resourceCodes';
import { isComputeResourceConfigured } from '../domain/summary';
import { MODAL_PATHS, MODAL_VALIDATE_FIELDS, resolveModalCpuValidateFields } from '../modal/modalPaths';
import { useComputeModalCpuState } from '../modal/useComputeModalCpuState';
import type { ComputeModalResourceKind } from '../types';

export interface ComputeResourceConfigModalProps {
  activeModal: ComputeModalResourceKind;
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  initialValues?: Record<string, unknown>;
  architecture?: string;
  resourceCodes: ReturnType<typeof resolveComputeResourceCodes>;
  gpuVariant: 'cloud-host' | 'container';
  memoryMin: number;
  memoryMax: number;
  memoryMarkCount: number;
  cpuMax: number;
  cpuMin: number;
  cpuSimpleMode: boolean;
  workloadType: 'VirtualMachine' | 'ContainerInstance';
  requireGpuSoftware?: boolean;
  onOk: (values: Record<string, unknown>) => void | Promise<void>;
  onCancel: () => void;
}

function ComputeModalCpuFields({ form, cpuMax, coreLabel }: { form: FormInstance; cpuMax: number; coreLabel: string }) {
  const { t } = useTranslation('cluster');
  const { cpuErr, cpuTotal, checkCpuLimit } = useComputeModalCpuState(form, MODAL_PATHS, cpuMax, t);

  return (
    <CpuConfigFields
      paths={MODAL_PATHS}
      cpuMax={cpuMax}
      cpuErr={cpuErr}
      checkCpuLimit={checkCpuLimit}
      cpuTotal={cpuTotal}
      coreLabel={coreLabel}
    />
  );
}

/** CPU / 内存 / GPU 资源配置弹窗 */
export function ComputeResourceConfigModal({
  activeModal,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  initialValues,
  architecture,
  resourceCodes,
  gpuVariant,
  memoryMin,
  memoryMax,
  memoryMarkCount,
  cpuMax,
  cpuMin,
  cpuSimpleMode,
  workloadType,
  requireGpuSoftware = gpuVariant !== 'container',
  onOk,
  onCancel,
}: ComputeResourceConfigModalProps) {
  const { t } = useTranslation('cluster');
  const isGpu = activeModal === 'gpu';
  const inventoryType = resourceCodes[activeModal];
  const vendorDeviceHidden = !billModelId && (activeModal === 'cpu' || activeModal === 'memory');
  const cpuValidateFields = activeModal === 'cpu' ? resolveModalCpuValidateFields(cpuSimpleMode) : MODAL_VALIDATE_FIELDS[activeModal];
  const vendorPrefix = resolveVendorDevicePath(activeModal, MODAL_PATHS);
  /** 自定义下单补填数量：已有厂商选型时锁定，避免改计费绑定 */
  const vendorDeviceDisabled =
    resourceEditMode === 'order-custom' &&
    hasVendorDeviceSelection(readVendorDeviceMeta((initialValues ?? {}) as Record<string, unknown>, vendorPrefix));
  const defaultUnit =
    activeModal === 'cpu'
      ? DEFAULT_QUANTITY_UNITS.cpu
      : activeModal === 'memory'
        ? DEFAULT_QUANTITY_UNITS.memory
        : DEFAULT_QUANTITY_UNITS.gpu;

  return (
    <ResourceConfigModal
      visible
      title={COMPUTE_RESOURCE_MODAL_TITLE[activeModal]}
      resourceCode={resourceCodes[activeModal]}
      vendorDevicePrefix={vendorPrefix}
      initialValues={initialValues}
      architecture={architecture}
      billModelId={billModelId}
      deploymentTypeId={deploymentTypeId}
      paymentTiming={paymentTiming}
      skuType={skuType}
      resourceEditMode={resourceEditMode}
      vendorDeviceHidden={vendorDeviceHidden}
      vendorDeviceDisabled={vendorDeviceDisabled}
      validateFields={cpuValidateFields}
      canSubmit={(values) =>
        isComputeResourceConfigured(activeModal, values, MODAL_PATHS, billModelId, paymentTiming, skuType, resourceEditMode)
      }
      inventoryResourceType={inventoryType}
      workloadType={workloadType}
      inventoryShowQuantity={isGpu && !billModelId}
      isContainerGpu={isGpu && gpuVariant === 'container'}
      requireGpuSoftware={requireGpuSoftware}
      quantityField={isGpu && billModelId ? `${MODAL_PATHS.gpu}.number` : undefined}
      quantityDefaultUnit={defaultUnit}
      lockQuantityDisplayToDefault={activeModal === 'memory'}
      onOk={onOk}
      onCancel={onCancel}
    >
      {(modalForm, { quantityUnit }) => {
        if (activeModal === 'cpu') {
          const coreLabel = billModelId ? quantityUnit : t('core-pl');
          return cpuSimpleMode ? (
            <ContainerSimpleCpuFields
              form={modalForm}
              cpuRequestPath={MODAL_PATHS.cpuRequest!}
              cpuMin={cpuMin}
              cpuMax={cpuMax}
              quantityUnitLabel={coreLabel}
            />
          ) : (
            <ComputeModalCpuFields form={modalForm} cpuMax={cpuMax} coreLabel={coreLabel} />
          );
        }
        if (activeModal === 'memory') {
          return (
            <MemoryConfigFields
              paths={MODAL_PATHS}
              memoryMin={memoryMin}
              memoryMax={memoryMax}
              memoryMarkCount={memoryMarkCount}
              quantityUnit={quantityUnit}
            />
          );
        }
        return <GpuExtraPersistFields gpuPath={MODAL_PATHS.gpu} />;
      }}
    </ResourceConfigModal>
  );
}
